<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed, onUnmounted, ref, provide, watch } from "vue"
import { useRoute } from "vue-router"
import { useEventBus } from "@vueuse/core"
import { getSidebar } from "@core/layouts/sidebar-registry"
import { SIDEBAR_CONTEXT_KEY } from "@core/layouts/sidebar-context"
import { LAYOUT_SIDEBAR_EXPANDED_EVENT } from "@core/layouts/layout.events"
import { useResponsive } from "@core/composables"
import { useAuthStore } from "@core/stores/auth.store"

// ================================
// STATE
// ================================
const route = useRoute()
const { isMobile } = useResponsive()
const authStore = useAuthStore()
const isSidebarCollapsed = ref(false)
const busSidebarExpanded = useEventBus<boolean>(LAYOUT_SIDEBAR_EXPANDED_EVENT)

// ================================
// PROVIDE — SIDEBAR
// ================================
provide(SIDEBAR_CONTEXT_KEY, {
    collapsed: isSidebarCollapsed,
    toggle: () => {
        isSidebarCollapsed.value = !isSidebarCollapsed.value
    },
    collapse: () => {
        isSidebarCollapsed.value = true
    },
})

// ================================
// WATCHERS
// ================================
/** Sur mobile : sidebar réduite par défaut ; repasse réduite quand on repasse en vue mobile. */
watch(
    isMobile,
    (mobile) => {
        if (mobile) {
            isSidebarCollapsed.value = true
        }
    },
    { immediate: true }
)

// ================================
// COMPUTED
// ================================
/** Clé meta de la route pour résoudre le composant sidebar enregistré. */
const getSidebarKey = computed(() => (route.meta.sidebar as string) ?? "")
/** Composant Vue associé à la sidebar courante (ou null). */
const getSidebarComponent = computed(() => getSidebar(getSidebarKey.value))

/** Sidebar affichée et développée : le bouton support doit passer sous le calque `z-1` de l’aside. */
watch(
    () => Boolean(getSidebarComponent.value) && !isSidebarCollapsed.value,
    (expanded) => {
        busSidebarExpanded.emit(expanded)
    },
    { immediate: true }
)

onUnmounted(() => {
    busSidebarExpanded.emit(false)
})

/**
 * Clic sur la zone sidebar : développe la barre si elle était repliée (hors boutons dédiés).
 * @param event - Cible du clic pour ignorer les toggles explicites
 */
function onSidebarAreaClick(event: MouseEvent) {
    const target = event.target as HTMLElement
    if (target.closest("[data-sidebar-toggle]")) {
        return
    }
    if (!isSidebarCollapsed.value) {
        return
    }
    // Lien ou bouton : ne pas rouvrir la barre. Sur mobile, après navigation, `collapse()`
    // a déjà été appelé et la remontée du clic déclencherait sinon un expand intempestif.
    if (target.closest("a[href], button")) {
        return
    }
    isSidebarCollapsed.value = false
}
</script>

<template>
    <div class="flex h-full min-h-0 flex-col min-w-0">
        <header class="sticky top-0 z-[1100] shrink-0">
            <HeaderNavigation />
            <BannerPastDuePayment v-if="authStore.hasUnpaidSubscriptions" />
        </header>
        <div class="flex min-h-0 flex-1 min-w-0">
            <aside
                v-if="getSidebarComponent"
                class="flex h-full shrink-0 flex-col border-r border-surface-200 bg-white shadow-[2px_0_8px_rgba(0,0,0,0.06)] transition-[width] duration-200 ease-out overflow-hidden z-1"
                :class="
                    isSidebarCollapsed ? 'w-16' : isMobile ? 'w-full' : 'w-80'
                "
                @click="onSidebarAreaClick"
            >
                <component :is="getSidebarComponent" />
            </aside>
            <main class="min-h-0 min-w-0 flex-1 overflow-auto">
                <RouterView />
            </main>
        </div>
    </div>
</template>
